import React, { useEffect, useState } from "react";
import { Form, Button, Modal, Spin, message } from "antd";
import API_MANAGER from "../../../API";
import ExperienceFormBlock from "./ExperienceFormBlock";
import {
  buildExperiencePayload,
  createEmptyExperienceForm,
  getCreatedRecordId,
  getExperienceId,
  mapApiExperienceToForm,
} from "../../../components/common/campaign/experienceHelpers";

async function syncRelatedExperiences({
  parentId,
  relatedItems,
  originalRelatedIds,
  userId,
  profileId,
}) {
  const currentIds = relatedItems
    .map((item) => item.id)
    .filter(Boolean);
  const toDelete = originalRelatedIds.filter((id) => !currentIds.includes(id));

  for (const id of toDelete) {
    await API_MANAGER.deleteCandidateExperience(id, userId);
  }

  for (const item of relatedItems) {
    const payload = buildExperiencePayload(item, {
      userId,
      parentId,
      profileId,
    });
    if (item.id) {
      await API_MANAGER.editCandidateExperience(
        buildExperiencePayload(item, {
          userId,
          parentId,
          profileId,
          isEdit: true,
        }),
        item.id
      );
    } else {
      await API_MANAGER.createCandidateExperience(payload);
    }
  }
}

function AddExperience({
  addExperience,
  setAddExperience,
  jobTitle,
  industry,
  discipline,
  induction,
  skills,
  userId,
  profileId,
  existingData,
  onSuccess,
}) {
  const [experienceForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [expandedName, setExpandedName] = useState(0);
  const [originalRelatedIds, setOriginalRelatedIds] = useState([]);
  const parentEmployer = Form.useWatch(["experiences", 0, "employerName"], experienceForm);

  const hydrateForm = () => {
    if (existingData) {
      const parent = mapApiExperienceToForm(existingData, { isParent: true });
      const related = (existingData.relatedExperiences || []).map((item) =>
        mapApiExperienceToForm(item, {
          isParent: false,
          employerName: existingData.employerName,
        })
      );
      experienceForm.setFieldsValue({
        experiences: [parent, ...related],
      });
      setOriginalRelatedIds(
        (existingData.relatedExperiences || [])
          .map((item) => getExperienceId(item))
          .filter(Boolean)
      );
      setExpandedName(0);
      return;
    }

    experienceForm.setFieldsValue({
      experiences: [createEmptyExperienceForm({ isParent: true, localId: "parent" })],
    });
    setOriginalRelatedIds([]);
    setExpandedName(0);
  };

  useEffect(() => {
    if (addExperience) {
      hydrateForm();
    }
  }, [addExperience, existingData]);

  useEffect(() => {
    if (!addExperience || existingData || parentEmployer === undefined) {
      return;
    }
    const experiences = experienceForm.getFieldValue("experiences") || [];
    if (experiences.length < 2) return;
    const needsSync = experiences.some(
      (item, index) => index > 0 && item?.employerName !== parentEmployer
    );
    if (!needsSync) return;
    experienceForm.setFieldsValue({
      experiences: experiences.map((item, index) =>
        index === 0 ? item : { ...item, employerName: parentEmployer }
      ),
    });
  }, [parentEmployer, addExperience, existingData, experienceForm]);

  const closeModal = () => {
    setAddExperience(false);
    experienceForm.resetFields();
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const experiences = values.experiences || [];
      const parentValues = experiences[0];
      const relatedItems = experiences.slice(1);
      const parentPayload = buildExperiencePayload(parentValues, {
        userId,
        profileId,
      });

      let parentId = parentValues?.id;
      if (parentId) {
        await API_MANAGER.editCandidateExperience(
          buildExperiencePayload(parentValues, {
            userId,
            profileId,
            isEdit: true,
          }),
          parentId
        );
      } else {
        const response = await API_MANAGER.createCandidateExperience(parentPayload);
        parentId = getCreatedRecordId(response);
      }

      if (!parentId) {
        throw new Error("Parent experience id missing");
      }

      await syncRelatedExperiences({
        parentId,
        relatedItems,
        originalRelatedIds,
        userId,
        profileId,
      });

      message.success(
        existingData
          ? "Successfully updated experience"
          : "Successfully added experience"
      );
      closeModal();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      message.error(
        error?.response?.data?.message?.message ||
          error?.message ||
          "An error occurred"
      );
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = () => {
    message.error("Please fill in all mandatory fields");
  };

  return (
    <Modal
      centered
      footer={false}
      open={addExperience}
      title={existingData ? "Professional Experience Edit" : "Experience Add"}
      className="resumeModal experienceAddModal"
      onCancel={closeModal}
      destroyOnClose
    >
      <Form
        form={experienceForm}
        layout="vertical"
        requiredMark={false}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
        validateTrigger="onSubmit"
        initialValues={{
          experiences: [createEmptyExperienceForm({ isParent: true })],
        }}
      >
        <Spin spinning={loading} />
        <Form.List name="experiences">
          {(fields, { add, remove }) => (
            <>
              {fields.map((field) => (
                <ExperienceFormBlock
                  key={field.key}
                  field={field}
                  form={experienceForm}
                  isParent={field.name === 0}
                  employerLocked={field.name !== 0}
                  expanded={expandedName === field.name}
                  onToggle={() => setExpandedName(field.name)}
                  onRemove={() => {
                    remove(field.name);
                    setExpandedName(0);
                  }}
                  jobTitle={jobTitle}
                  industry={industry}
                  discipline={discipline}
                  induction={induction}
                  skills={skills}
                />
              ))}
              <Button
                className="addRelatedExperienceBtn"
                type="dashed"
                block
                onClick={() => {
                  const employerName =
                    experienceForm.getFieldValue(["experiences", 0, "employerName"]) ||
                    "";
                  add(
                    createEmptyExperienceForm({
                      isParent: false,
                      employerName,
                    })
                  );
                  setExpandedName(fields.length);
                }}
              >
                Add related experience
              </Button>
            </>
          )}
        </Form.List>
        <div className="buttonCol" style={{ marginTop: 16 }}>
          <Button className="btn1" htmlType="submit">
            {existingData ? "Update" : "Create"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
}

export default AddExperience;
